import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from '../../../core';

const LEGAL_PATHS = new Set([
  '/terms',
  '/privacy',
  '/install-terms',
  '/equipment-terms',
  '/renewal-terms',
  '/terms-and-conditions',
  '/privacy-policy',
]);

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, NgOptimizedImage]
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  // Use computed values for reactive state
  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly currentUser = this.authService.currentUser;

  private readonly urlPath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  private readonly pathOnly = computed(() =>
    (this.urlPath() ?? '').split('?')[0]
  );

  /** Public onboarding: logo only (hide sign-in / welcome / sign-out). */
  protected readonly isOnboardingShell = computed(() =>
    this.pathOnly().startsWith('/onboarding')
  );

  /** Public legal pages visited by customers who cannot use staff login. */
  private readonly isLegalShell = computed(() =>
    LEGAL_PATHS.has(this.pathOnly())
  );

  /** Welcome + Sign Out when signed in (not on onboarding invite pages). */
  protected readonly showAuthenticatedControls = computed(
    () => this.isAuthenticated() && !this.isOnboardingShell()
  );

  /**
   * Header Sign In for unauthenticated staff entry points only.
   * Hidden on legal, product-terms, onboarding, and /login (Amplify form is enough).
   */
  protected readonly showSignIn = computed(() => {
    if (this.isAuthenticated() || this.isOnboardingShell() || this.isLegalShell()) {
      return false;
    }
    return this.pathOnly() !== '/login';
  });

  /** Logo target: login when signed out; Simplify for Admin, Guardian otherwise. */
  protected readonly logoLink = computed(() => {
    if (!this.isAuthenticated()) {
      return '/login';
    }
    return this.authService.isAdmin() ? '/simplify' : '/guardian';
  });

  protected readonly logoAriaLabel = computed(() => {
    if (!this.isAuthenticated()) {
      return 'Go to sign in';
    }
    return this.authService.isAdmin()
      ? 'Go to Simplify dashboard'
      : 'Go to Guardian';
  });

  // Computed display name: first name if available, otherwise username
  protected readonly displayName = computed(() => {
    const user = this.currentUser();
    if (!user) return '';

    // Return first name if available and not null/empty, otherwise username
    return (user.first_name && user.first_name.trim() !== '')
      ? user.first_name
      : user.username;
  });

  protected async signOut(): Promise<void> {
    await this.authService.signOut();
    // Redirect to home page after logout to ensure user isn't on a protected route
    await this.router.navigate(['/']);
  }
}
